import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("category") || "all";
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(initialCat);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("relevance");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products/categories").then((r) => setCategories(["all", ...r.data]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category !== "all") p.category = category;
    if (q) p.q = q;
    api.get("/products", { params: p }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [category, q]);

  useEffect(() => {
    if (category === "all") { params.delete("category"); setParams(params); }
    else { params.set("category", category); setParams(params); }
    // eslint-disable-next-line
  }, [category]);

  const sorted = useMemo(() => {
    const c = [...products];
    if (sort === "price_asc") c.sort((a, b) => a.price - b.price);
    if (sort === "price_desc") c.sort((a, b) => b.price - a.price);
    if (sort === "discount") c.sort((a, b) => {
      const da = a.original_price ? (1 - a.price / a.original_price) : 0;
      const db = b.original_price ? (1 - b.price / b.original_price) : 0;
      return db - da;
    });
    return c;
  }, [products, sort]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
      <div className="mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--bs-red)] mb-3">Catalogo</div>
        <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tighter">
          {category === "all" ? "Tutti i prodotti" : category}
        </h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <aside className="lg:w-64 shrink-0 space-y-8">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] mb-3 block">Cerca</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--bs-muted)]" />
              <Input
                data-testid="shop-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca prodotto..."
                className="pl-9 rounded-none border-black"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] mb-3 block">Categoria</label>
            <ul className="space-y-1">
              {categories.map((c) => (
                <li key={c}>
                  <button
                    data-testid={`filter-cat-${c}`}
                    onClick={() => setCategory(c)}
                    className={`w-full text-left px-3 py-2 text-sm border transition-colors ${category === c ? "border-[var(--bs-red)] bg-[var(--bs-red)] text-white font-semibold" : "border-transparent hover:border-black"}`}
                  >
                    {c === "all" ? "Tutte le categorie" : c}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] mb-3 block">Ordina per</label>
            <select
              data-testid="shop-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="w-full border border-black px-3 py-2 text-sm bg-white"
            >
              <option value="relevance">Rilevanza</option>
              <option value="price_asc">Prezzo crescente</option>
              <option value="price_desc">Prezzo decrescente</option>
              <option value="discount">Sconto maggiore</option>
            </select>
          </div>
        </aside>

        <div className="flex-1">
          <div className="mb-6 flex items-center justify-between text-sm text-[var(--bs-muted)]">
            <span data-testid="shop-count">{loading ? "Caricamento…" : `${sorted.length} prodotti`}</span>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bs-card aspect-[3/4] animate-pulse bg-[var(--bs-surface)]" />
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <div className="border border-dashed border-[var(--bs-border)] p-16 text-center">
              <div className="font-display font-bold text-2xl mb-2">Nessun prodotto trovato</div>
              <p className="text-[var(--bs-muted)]">Prova a modificare i filtri.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {sorted.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
